import { Component, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export type ButtonVariant = 'basic' | 'stroked' | 'flat' | 'icon';
export type ButtonColor = 'primary' | 'accent' | 'warn' | undefined;
export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonTarget = '_self' | '_blank' | '_parent' | '_top';
export type QueryParamsHandling = 'merge' | 'preserve' | null;

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, RouterModule, MatProgressSpinnerModule],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  host: {
    'tabindex': '-1' 
  }
})
export class ButtonComponent {
  /** Variantes del botón (basic, stroked, flat, icon); Default = basic */
  variant = input<ButtonVariant>('basic');
  // ToDo: Falta implementar el cambio de color en base a la paleta
  color = input<ButtonColor>('primary');

  /** Estado deshabilitado (true, false); Default = false */
  disabled = input<boolean>(false);

  /** Estado de carga (true, false); Default = false */
  loading = input<boolean>(false);

  /** Tipo de botón (button, submit, reset); Default = button */
  type = input<ButtonType>('button');

  // Para enlaces (<a>)
  /** URL del enlace (pagina externa) */
  href = input<string | null>(null);

  /** URL del enlace (pagina interna) */
  routerLink = input<string | string[] | null>(null);

  /** Parámetros de consulta para el enlace */
  queryParams = input<Record<string, string | number | boolean> | null>(null);

  /** Modo de manejo de parámetros de consulta */
  queryParamsHandling = input<QueryParamsHandling>(null);

  /** Donde abrir el enlace; Default = _self */
  target = input<ButtonTarget>('_self'); // Donde abrir el enlace

  /** Icono del botón (nombre del icono de Material Icons) */
  icon = input<string | null>(null);

  /** Etiqueta ARIA del botón */
  ariaLabel = input<string | null>(null);

  /** Clases personalizadas para el botón */
  customClass = input<string>('');

  /** Evento de clic en el botón */
  btnClick = output<Event>();

  // Computeds
  isLink = computed(() => !!(this.href() || this.routerLink()));
  gifSource = computed(() => {
    if (this.variant() === 'flat') {
      return 'images/loading-white.gif';
    }
    return 'images/loading.gif';
  });

  effectiveAriaLabel = computed(() => {
    return this.ariaLabel() || this.getDefaultAriaLabel();
  });

  showIcon = computed(() => {
    return !!(this.icon() && !this.loading());
  });

  showContent = computed(() => {
    const variant = this.variant();
    return !['icon', 'fab', 'mini-fab'].includes(variant);
  });

  computedClasses = computed(() => {
    const classes: string[] = [];

    // Estado de carga
    if (this.loading()) {
      classes.push('button-loading');
    }

    // Clases personalizadas
    if (this.customClass()) {
      classes.push(this.customClass());
    }

    return classes.join(' ');
  });

  // --------------- Metodos --------------- //
  // Retorna un evento al momento de pulsar el boton
  onClick(event: Event): void {
    if (!this.disabled() && !this.loading()) {
      this.btnClick.emit(event);
    }
  }

  // Retorna la etiqueta ARIA por defecto (mejora para accesibilidad)
  private getDefaultAriaLabel(): string {
    if (this.loading()) {
      return 'Loading...';
    }

    const variant = this.variant();
    if (['icon', 'fab', 'mini-fab'].includes(variant)) {
      return 'Button';
    }

    return '';
  }
}
